import { reactive } from 'vue'
import i18n from '@/i18n'

/**
 * Confirm dialog manager — Vue port of `lxConfirm()`.
 * Returns a Promise<boolean> so call sites can `await` the user's choice.
 */
export interface ConfirmOptions {
  title?: string
  message?: string
  confirmLabel?: string
  cancelLabel?: string
  danger?: boolean
  icon?: string | null
  bodyHTML?: string
}

const state = reactive<{
  open: boolean
  options: Required<ConfirmOptions> | null
  _resolve: ((value: boolean) => void) | null
}>({
  open: false,
  options: null,
  _resolve: null,
})

function confirm(opts: ConfirmOptions = {}): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    /* The defaults are read when the dialog opens, in the language the
     * reader has at that moment. */
    const { t } = i18n.global
    state.options = {
      title: t('ui.confirm.title'),
      message: t('ui.confirm.message'),
      confirmLabel: t('common.confirm'),
      cancelLabel: t('common.cancel'),
      danger: false,
      icon: null,
      bodyHTML: '',
      ...opts,
    }
    state._resolve = resolve
    state.open = true
  })
}

function resolve(value: boolean): void {
  state.open = false
  if (state._resolve) {
    state._resolve(!!value)
    state._resolve = null
  }
}

export function useConfirm() {
  return { state, confirm, resolve }
}
